import { strideAccountAtom } from '@/atoms/Wallet'
import { Space } from '@mantine/core'
import { useAtomValue } from 'jotai'
import dynamic from 'next/dynamic'
import React from 'react'
import { useEligibleAirdropsQuery } from '@/queries'
import { AirdropError } from './AirdropError'
import { AirdropIneligible } from './AirdropIneligible'
import { AirdropLandingPage } from './AirdropLandingPage'
import { AirdropSkeleton } from './AirdropSkeleton'
import { AirdropClaimHistory, AirdropGuideLink, AirdropInformation, AirdropTasks } from './AirdropTasks'
import { useAirdrop } from './AirdropProvider'
import { useRebateProgramQuery, useTaskStatusQuery, useTotalClaimableQuery } from './queries'
import { AirdropContainer, AirdropHeading, AirdropTitle } from './shared'

const AirdropConnectWallet = dynamic(
  () => import('./AirdropConnectWallet').then((module) => module.AirdropConnectWallet),
  { ssr: false }
)

const Airdrop: React.FC = () => {
  const strideAccount = useAtomValue(strideAccountAtom)

  const { screen } = useAirdrop()
  // @TODO: Handle error
  const {
    data: totalClaimable,
    isLoading: isTotalClaimableLoading,
    error: totalClaimableError
  } = useTotalClaimableQuery()

  // @TODO: Handle error
  const { data: rebateProgram, isLoading: isRebateProgramLoading, error: rebateProgramError } = useRebateProgramQuery()

  const { data: taskStatus, isLoading: isTaskStatusLoading, error: taskStatusError } = useTaskStatusQuery()

  const { isLoading: isEligibleAirdropsLoading, error: eligibleAirdropsError } = useEligibleAirdropsQuery()

  const isAnyQueryFailing = rebateProgramError || totalClaimableError || taskStatusError || eligibleAirdropsError

  const isEligibleLoading = isTotalClaimableLoading || isRebateProgramLoading

  const isEligible = Boolean(totalClaimable?.coins.length || rebateProgram?.status !== 'ineligible')

  const isAnyTaskInProgress = taskStatus?.claim || taskStatus?.liquidStake || taskStatus?.stake

  const isRebateProgramInProgress = rebateProgram?.status === 'pending' || rebateProgram?.status === 'claimed'

  const isForcedThroughLandingPage = !isAnyTaskInProgress && !isRebateProgramInProgress

  const isLandingPageActive = screen === 'default' ? isForcedThroughLandingPage : screen === 'landing'

  if (!strideAccount || isEligibleLoading || isEligibleAirdropsLoading) {
    // We'll skip checking for selectedAccount here so we don't flash the connect wallet state
    // We'll use the "loading" state for the wallet connection for while eligibilty loads
    // Less effort so we don't unnecessarily flash the airdrop task list while eligibility hasn't been fully loaded yet.
    return (
      <>
        <AirdropTitle />
        <AirdropConnectWallet />
      </>
    )
  }

  if (isAnyQueryFailing) {
    return (
      <>
        <AirdropTitle />
        <AirdropContainer narrow>
          <AirdropHeading />
          <Space h="md" />
          <AirdropError />
        </AirdropContainer>
      </>
    )
  }

  if (isEligible && isLandingPageActive) {
    return (
      <>
        <AirdropTitle />
        <AirdropLandingPage />
      </>
    )
  }

  if (!isEligible) {
    return (
      <>
        <AirdropTitle />
        <AirdropIneligible />
      </>
    )
  }

  if (isTaskStatusLoading) {
    return (
      <>
        <AirdropTitle />
        <AirdropContainer narrow>
          <AirdropHeading />
          <Space h="md" />
          <AirdropSkeleton />
        </AirdropContainer>
      </>
    )
  }

  return (
    <>
      <AirdropTitle />

      <AirdropContainer narrow>
        <AirdropHeading />
        <Space h="md" />
        <AirdropInformation />
        <Space h="lg" />
        <AirdropTasks />
        <Space h="lg" />
        <AirdropClaimHistory />
        <Space h="lg" />
        <AirdropGuideLink />
      </AirdropContainer>
    </>
  )
}

export { Airdrop }
